import type { LanguageCodeType } from '@healthhub/react-ui';
import { changeLanguage, DEFAULT_LANG, LANGUAGE_CODE, twClassMerge  } from '@healthhub/react-ui';
import { Skeleton, Typography } from 'antd';
import type { KeyboardEvent } from 'react';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { i18n as i18nInstance } from 'i18n';

import { useLayoutViewContext } from '@shared/context';
import { useExtendedRouter } from '@shared/hooks/useExtendedRouter';
import useIsAuthenticated from '@shared/hooks/useIsAuthenticated';
import { useGetPreferredLanguageQuery, useUpdatePreferredLanguageMutation } from '@shared/services/language';


const { Text } = Typography;

interface LanguageSelectionProps {
  className?: string;
}

const AVAILABLE_LANGUAGES = [
  {
    label: 'English',
    value: LANGUAGE_CODE.en,
  },
  {
    label: '中文',
    value: LANGUAGE_CODE.zh,
  },
  {
    label: 'Melayu',
    value: LANGUAGE_CODE.ms,
  },
  {
    label: 'தமிழ்',
    value: LANGUAGE_CODE.ta,
  },
];

const LanguageSelection = ({ className }: LanguageSelectionProps) => {
  const { isPortalView, isMobileView } = useLayoutViewContext();

  const { isAuthenticated } = useIsAuthenticated();

  const { i18n, t } = useTranslation();
  const { availableLanguages } = useExtendedRouter();

  const [updatePreferredLanguage, { isLoading: isUpdatePreferredLanguage }] = useUpdatePreferredLanguageMutation();
  const { data, isFetching: isFetchingPreferredLanguage } = useGetPreferredLanguageQuery(undefined, {
    skip: !isAuthenticated,
  });

  const onChangeLanguage = useCallback(
    (language: string, skipUpdate = false) => {
      changeLanguage(language, i18nInstance);
      localStorage.removeItem('orgLng');
      if (!skipUpdate && isAuthenticated && (isPortalView || isMobileView)) {
        updatePreferredLanguage({ Language: language });
      }
    },
    [isAuthenticated, updatePreferredLanguage, isPortalView, isMobileView]
  );

  const onKeyDownLanguage = (e: KeyboardEvent<HTMLButtonElement>, language: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onChangeLanguage(language);
    }
  };

  useEffect(() => {
    if (data && availableLanguages?.includes(data.Language as LanguageCodeType)) {
      onChangeLanguage(data.Language, true);
    }
  }, [data, onChangeLanguage, availableLanguages]);

  if (!availableLanguages) {
    return null;
  }

  if (isUpdatePreferredLanguage || isFetchingPreferredLanguage) {
    return <Skeleton.Button active className={twClassMerge('flex h-5 w-80', className)} />;
  }

  const languageList = AVAILABLE_LANGUAGES.filter(
    ({ value }) => availableLanguages.includes(value) || value === DEFAULT_LANG
  );

  return (
    <div className={twClassMerge('flex gap-2 items-center flex-wrap', className)}>
      <Text className="text-sm">{t('alsoAvailableIn')}</Text>
      <div className="flex gap-2">
        {languageList.map(({ label, value }, index) => {
          const isSelected = i18n.language === value;

          return (
            <React.Fragment key={value}>
              <button
                className={twClassMerge('text-primary-green-4 text-sm', {
                  'text-primary-blue-grey-4 underline': isSelected,
                })}
                disabled={isSelected}
                onClick={() => onChangeLanguage(value)}
                onKeyDown={(e) => onKeyDownLanguage(e, value)}
              >
                {label}
              </button>
              {index !== languageList.length - 1 && <Text className="text-sm text-functional-stone">|</Text>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageSelection;
